/**
 * lib/firebase.ts
 * ------------------------------------------------------------------
 * Firebase initialisation plus undefined-safe Firestore write
 * wrappers. Mirrors the LKC app's proven pattern so both codebases
 * behave identically at the write boundary.
 *
 * Every write to Firestore (setDoc, addDoc, updateDoc) goes through a
 * wrapper that runs the payload through `stripUndefined` before
 * delegating to the real SDK. Firestore rejects `undefined` field
 * values anywhere in a payload, and TypeScript can't catch the cases
 * where an optional field resolves to undefined at runtime.
 *
 * Callers only change their import path:
 *     import { setDoc } from '@/lib/firebase';   // not 'firebase/firestore'
 *
 * Firestore sentinels (serverTimestamp, arrayUnion, increment, …)
 * pass through untouched — `stripUndefined` detects them by their
 * `_methodName` discriminator.
 *
 * NOTE: writeBatch / runTransaction operations are NOT auto-wrapped.
 * Call `stripUndefined` explicitly on those payloads (see
 * lib/numbering.ts for the transaction pattern).
 * ------------------------------------------------------------------
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getFirestore,
  type Firestore,
  setDoc as fsSetDoc,
  addDoc as fsAddDoc,
  updateDoc as fsUpdateDoc,
  type DocumentReference,
  type CollectionReference,
  type DocumentData,
  type SetOptions,
  type WithFieldValue,
  type PartialWithFieldValue,
  type UpdateData,
} from 'firebase/firestore';
import { getAuth, type Auth } from 'firebase/auth';
import { getFunctions, type Functions } from 'firebase/functions';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { stripUndefined } from '@/lib/sanitize';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env
    .VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};

const app: FirebaseApp = initializeApp(firebaseConfig);

export const db: Firestore = getFirestore(app);
export const auth: Auth = getAuth(app);
// Region pinned to us-central1 so v2 callable functions construct the
// correct Cloud Run URLs (same fix as the LKC app — without it the SDK
// tries the legacy v1 URL pattern and fails CORS preflight).
export const functions: Functions = getFunctions(app, 'us-central1');
export const storage: FirebaseStorage = getStorage(app);

export default app;

/* ============================================================ */
/* Undefined-safe write wrappers                                  */
/* ============================================================ */

function cleanPayload<T>(data: T, fnName: string): T {
  if (data === null || data === undefined) {
    throw new Error(
      `${fnName} received a null/undefined payload. Refusing to write — ` +
        `this almost certainly indicates a bug in the calling code.`,
    );
  }
  if (typeof data !== 'object') {
    return data;
  }
  return stripUndefined(
    data as unknown as Record<string, unknown>,
  ) as unknown as T;
}

export function setDoc<AppModelType, DbModelType extends DocumentData>(
  reference: DocumentReference<AppModelType, DbModelType>,
  data: WithFieldValue<AppModelType>,
): Promise<void>;
export function setDoc<AppModelType, DbModelType extends DocumentData>(
  reference: DocumentReference<AppModelType, DbModelType>,
  data: PartialWithFieldValue<AppModelType>,
  options: SetOptions,
): Promise<void>;
export function setDoc<AppModelType, DbModelType extends DocumentData>(
  reference: DocumentReference<AppModelType, DbModelType>,
  data: WithFieldValue<AppModelType> | PartialWithFieldValue<AppModelType>,
  options?: SetOptions,
): Promise<void> {
  const clean = cleanPayload(data, 'setDoc');
  if (options !== undefined) {
    return fsSetDoc(
      reference,
      clean as PartialWithFieldValue<AppModelType>,
      options,
    );
  }
  return fsSetDoc(reference, clean as WithFieldValue<AppModelType>);
}

export function addDoc<AppModelType, DbModelType extends DocumentData>(
  reference: CollectionReference<AppModelType, DbModelType>,
  data: WithFieldValue<AppModelType>,
): Promise<DocumentReference<AppModelType, DbModelType>> {
  const clean = cleanPayload(data, 'addDoc');
  return fsAddDoc(reference, clean as WithFieldValue<AppModelType>);
}

export function updateDoc<AppModelType, DbModelType extends DocumentData>(
  reference: DocumentReference<AppModelType, DbModelType>,
  data: UpdateData<DbModelType>,
): Promise<void> {
  const clean = cleanPayload(data, 'updateDoc');
  return fsUpdateDoc(reference, clean as UpdateData<DbModelType>);
}
